import type { Layer } from "@geosoft/viewer-sdk";
import {
  SLOT_LABEL,
  SLOT_ORDER,
  type SceneSlotName,
} from "@/app/layerMetadata";
import type {
  SceneHierarchyManifest,
  TreeNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import { isCatalogLayerVisibilityNode } from "./layerVisibilityNode";

const ATTACHED_ROOT_ID = "attached-layers-root";
const EXCLUDED_ATTACHED_SLOTS: ReadonlySet<string> = new Set(["environment"]);

function slotOf(
  layer: Layer,
  slotMap: Readonly<Record<string, string | undefined>>,
): SceneSlotName | null {
  const slot = slotMap[layer.id];
  if (slot && (SLOT_ORDER as readonly string[]).includes(slot)) {
    return slot as SceneSlotName;
  }
  const styleSlot = (layer.style as Record<string, unknown> | undefined)?.gtwinSlot;
  if (typeof styleSlot === "string" && (SLOT_ORDER as readonly string[]).includes(styleSlot)) {
    return styleSlot as SceneSlotName;
  }
  return null;
}

function layerLabel(layer: Layer): string {
  const label = (layer as { label?: unknown }).label;
  return typeof label === "string" && label.trim() ? label : layer.id;
}

function makeNode(
  id: string,
  parentId: string | null,
  overrides: Omit<TreeNode, "id" | "objectId" | "parentId" | "aspects">,
): TreeNode {
  return {
    id,
    objectId: id,
    parentId,
    aspects: {
      spatial: { parentId },
      functional: { parentId },
    },
    ...overrides,
  };
}

/**
 * Scene Tree V2 can be driven by a model/object-index manifest. In that path the
 * normal gTwin catalog layers (terrain, PMTiles, basemaps, overlays, etc.) may
 * disappear from the tree even though they are attached to the viewer. Add only
 * the missing catalog layers back into the SAME tree under an "Attached layers"
 * branch, preserving existing layer nodes and avoiding a separate duplicate UI.
 */
export function appendAttachedLayerNodes(
  manifest: SceneHierarchyManifest,
  layers: readonly Layer[],
  slotMap: Readonly<Record<string, string | undefined>>,
): SceneHierarchyManifest {
  const representedLayerIds = new Set<string>();
  for (const node of manifest.nodes) {
    if (node.layerId && isCatalogLayerVisibilityNode(node)) {
      representedLayerIds.add(node.layerId);
    }
  }

  const bySlot = new Map<SceneSlotName, Layer[]>();
  for (const layer of layers) {
    if (representedLayerIds.has(layer.id)) continue;
    const slot = slotOf(layer, slotMap);
    if (!slot || EXCLUDED_ATTACHED_SLOTS.has(slot)) continue;
    const items = bySlot.get(slot) ?? [];
    items.push(layer);
    bySlot.set(slot, items);
  }

  if (bySlot.size === 0) return manifest;

  const added: TreeNode[] = [
    makeNode(ATTACHED_ROOT_ID, null, {
      lor: 1,
      label: "Attached layers",
      kind: "folder",
      layerId: null,
      featureId: null,
      domain: "generic",
      standardRef: null,
      metadata: { isAttachedLayersRoot: true },
      hasChildren: true,
      sortKey: -100,
    }),
  ];

  for (const slot of SLOT_ORDER) {
    const slotLayers = bySlot.get(slot);
    if (!slotLayers || slotLayers.length === 0) continue;

    const slotId = `attached-slot:${slot}`;
    added.push(
      makeNode(slotId, ATTACHED_ROOT_ID, {
        lor: 2,
        label: SLOT_LABEL[slot],
        kind: "group",
        layerId: null,
        featureId: null,
        domain: "generic",
        standardRef: null,
        metadata: { isAttachedLayerSlot: true, slot },
        hasChildren: true,
        sortKey: SLOT_ORDER.indexOf(slot),
      }),
    );

    slotLayers
      .slice()
      .sort((a, b) => layerLabel(a).localeCompare(layerLabel(b)))
      .forEach((layer, index) => {
        added.push(
          makeNode(`attached-layer:${layer.id}`, slotId, {
            lor: 3,
            label: layerLabel(layer),
            kind: "asset",
            layerId: layer.id,
            featureId: null,
            domain: "generic",
            standardRef: null,
            metadata: {
              isAttachedLayer: true,
              sceneItemIds: [layer.id],
              mediaKind: layer.kind,
              layerKind: layer.kind,
              slot,
            },
            hasChildren: false,
            sortKey: index,
          }),
        );
      });
  }

  return {
    ...manifest,
    nodes: [...added, ...manifest.nodes],
    totalNodes: manifest.totalNodes + added.length,
  };
}
